// Resolve persisted Signal text-span evidence onto the reconstructed trace
// shown by ContextPanel. Each span is anchored in the exact projected prompt
// that produced it, relocated into the rendered turns by BLOCK identity --
// never by whole-conversation alignment, which loses every highlight at once
// the moment any single turn is re-projected (truncation, compaction, added
// turns) -- and accepted only when the surface's SHA-256 matches the digest
// the signal recorded. Historical rows never fall back to rerunning a
// detector in the browser.
//
// Every detection the dashboard lists emits this envelope
// (docs/specs/signals-contract.md, "Evidence envelope"), so one resolver serves
// them all: the row's namespace names the signal, its tag names the finding, and
// its spans say where. The dashboard carries no detector copies of its own.

import { toolUseProjection } from "./signalHighlight.js";
import { toolResultProjection } from "./traceToolCall.js";
import { CATALOG_SIGNALS } from "./triggers/exampleTriggers.js";

const VERIFIER_EXTENSION = "com.origin.credscan_verifier_score/v1";
// The judge stage writes its verdict onto every row it promotes, beside the
// classifier's score. One verdict per row (the judge decides rows, not
// sections), so it attaches to every highlight the row produces.
const JUDGE_EXTENSION = "com.origin.credscan_judge_verdict/v1";
const JUDGE_VERDICTS = new Set(["promote", "deny"]);

// The namespaces whose rows can mark a span: exactly the detections the
// dashboard lists (exampleTriggers.js, SIGNAL_BACKEND). Static and exact on
// purpose. A prefix match would also sweep in namespaces the catalog does not
// surface -- the retired `security.credscan.review` band still has rows inside
// its TTL, and endpoint-subject detections like `security.config_shadow`
// describe the machine rather than a span of this prompt -- and their evidence
// would then decide the "highlights unavailable" notice for a mark that was
// never going to render.
export const DETECTION_NAMESPACES = Object.freeze(CATALOG_SIGNALS.map((t) => t.family));
const IDENTITY_BY_NAMESPACE = new Map(CATALOG_SIGNALS.map((t) => [t.family, t]));

// Signals whose matched span is unreadable as rendered -- an invisible carrier
// run, a base64 blob -- and whose tag `value` is the decoded finding. That value
// rides the mark as `detail` so the tooltip can say what was found (markTip in
// ContextPanel). Other signals' values repeat the visible span or are structured
// summaries, and their tooltip stays the catalog description.
const DECODED_VALUE_NAMESPACES = new Set([
    "security.unicode_smuggling",
    "security.encoded_payload",
]);

// Namespace -> the dashboard command the mark is attributed to, and the accent
// family its `type` carries. Null for anything outside the listed detections.
// Pure and hydration-independent.
export function highlightIdentity(namespace) {
    const signal = IDENTITY_BY_NAMESPACE.get(namespace);
    if (!signal) return null;
    return {
        command: signal.command,
        // Mark accents key off the type's first segment (MARK_ACCENT in
        // ContextPanel): alerts tint red as `risk`, audit-tier findings muted.
        family: signal.tier === "audit" ? "audit" : "risk",
        detailFromValue: DECODED_VALUE_NAMESPACES.has(namespace),
    };
}

function parseEnvelope(raw) {
    let envelope = raw;
    if (typeof raw === "string") {
        if (!raw) return { kind: "legacy" };
        try { envelope = JSON.parse(raw); } catch { return { kind: "invalid" }; }
    }
    if (!envelope || typeof envelope !== "object") return { kind: "invalid" };
    if (envelope.version === 0) return { kind: "legacy" };
    if (envelope.version !== 1 || !Array.isArray(envelope.items)) return { kind: "invalid" };
    return { kind: "current", envelope };
}

function sectionScores(items) {
    const extension = (items || []).find((item) => (
        item?.extension?.type === VERIFIER_EXTENSION
    ))?.extension;
    const sections = extension?.data?.sections;
    if (!Array.isArray(sections)) return new Map();
    const scores = new Map();
    for (const result of sections) {
        if (!Number.isInteger(result?.section)) continue;
        if (typeof result.score === "number" && Number.isFinite(result.score)) {
            scores.set(result.section, result.score);
        }
    }
    return scores;
}

function judgeVerdict(items) {
    const data = (items || []).find((item) => (
        item?.extension?.type === JUDGE_EXTENSION
    ))?.extension?.data;
    if (!data || typeof data !== "object") return null;
    if (!JUDGE_VERDICTS.has(data.verdict)) return null;
    if (typeof data.confidence !== "number" || !Number.isFinite(data.confidence)) return null;
    const verdict = { verdict: data.verdict, confidence: data.confidence };
    if (typeof data.reason_code === "string" && data.reason_code) verdict.reasonCode = data.reason_code;
    if (typeof data.model_version === "string" && data.model_version) verdict.modelVersion = data.model_version;
    if (data.cached === true) verdict.cached = true;
    return verdict;
}

function blocks(turn) {
    return Array.isArray(turn?.blocks) ? turn.blocks : [];
}

function toolCallId(block) {
    return block?.id ?? block?.tool_use_id ?? block?.toolUseId ?? null;
}

function findToolBlock(turns, id, result) {
    for (let turn = 0; turn < turns.length; turn += 1) {
        const entries = blocks(turns[turn]);
        for (let block = 0; block < entries.length; block += 1) {
            const entry = entries[block];
            const matches = result
                ? entry?.type === "tool_result" && toolCallId(entry) === id
                : entry?.type === "tool_use" && toolCallId(entry) === id;
            if (matches) return { entry, turn, block };
        }
    }
    return null;
}

function eventPathBlock(turns, path) {
    const match = /^\/prompt\/turns\/(\d+)\/blocks\/(\d+)$/.exec(path || "");
    if (!match) return null;
    const turn = Number(match[1]);
    const block = Number(match[2]);
    const entry = blocks(turns[turn])[block];
    return entry ? { entry, turn, block } : null;
}

function sourceSurface(span, event) {
    const turns = Array.isArray(event?.turns) ? event.turns : [];
    const ref = span?.object;
    if (!ref || typeof ref !== "object") return null;
    if (ref.source_event_id !== String(event?.event_id ?? "")) return null;

    if (span.surface === "user_text" && !span.field_path) {
        return typeof event?.text === "string"
            ? { key: "user_text", label: "User prompt", text: event.text }
            : null;
    }

    let found = null;
    if (typeof ref.tool_call_id === "string") {
        found = findToolBlock(turns, ref.tool_call_id, span.surface === "tool_result_content");
    } else if (ref.message && Number.isInteger(ref.message.turn_ordinal) && Number.isInteger(ref.message.block_ordinal)) {
        const turn = ref.message.turn_ordinal;
        const block = ref.message.block_ordinal;
        const entry = blocks(turns[turn])[block];
        if (entry) found = { entry, turn, block };
    } else if (typeof ref.event_path === "string") {
        found = eventPathBlock(turns, ref.event_path);
    }
    if (!found) return null;

    const role = String(turns[found.turn]?.role || "message");
    const key = `${span.surface}:${found.turn}:${found.block}:${span.field_path || ""}`;
    if (span.surface === "message_text" && found.entry?.type === "text" && !span.field_path) {
        const label = role === "user" ? "User prompt" : `${role[0]?.toUpperCase() || "M"}${role.slice(1)} message`;
        return typeof found.entry.text === "string" ? { key, label, text: found.entry.text } : null;
    }
    if (span.surface === "tool_call_argument" && found.entry?.type === "tool_use") {
        const text = pointer(found.entry.input, span.field_path || "");
        const tool = found.entry.name ? `: ${found.entry.name}` : "";
        return typeof text === "string" ? { key, label: `Tool call${tool}`, text } : null;
    }
    if (span.surface === "tool_result_content" && found.entry?.type === "tool_result") {
        const text = pointer(found.entry.content, span.field_path || "");
        const tool = found.entry.linkedToolName ? `: ${found.entry.linkedToolName}` : "";
        return typeof text === "string" ? { key, label: `Tool result${tool}`, text } : null;
    }
    return null;
}

function pointer(root, path) {
    if (!path) return root;
    if (typeof path !== "string" || !path.startsWith("/")) return undefined;
    return path.slice(1).split("/").reduce((value, token) => {
        if (value == null) return undefined;
        const key = token.replace(/~1/g, "/").replace(/~0/g, "~");
        return value[key];
    }, root);
}


// Locate one projected block in the RENDERED turns by block identity.
//
// Every candidate is byte-identical to the block the span was computed against
// (sameBlock), so whichever is chosen carries the right CONTENT; among duplicates
// (a retried tool call, a re-pasted message) the occurrence nearest the position
// the projection predicts wins, preferring the span's own block ordinal. Zero
// candidates fails THIS span only -- resolution is per-span, never global.
// One stringify per rendered block, built once per resolve pass and reused
// across every span, instead of re-scanning (and re-stringifying) all blocks for
// each span. persistedSignalHighlights now resolves rows from all nine catalog
// namespaces, so a tool-heavy trace can carry many spans; the index keeps the
// block match a hash lookup rather than an O(spans x blocks) rescan.
function renderedBlockIndex(renderedTurns, context) {
    if (context.renderedIndex && context.renderedIndex.turns === renderedTurns) {
        return context.renderedIndex.byKey;
    }
    const byKey = new Map();
    for (let turn = 0; turn < renderedTurns.length; turn += 1) {
        const entries = blocks(renderedTurns[turn]);
        for (let block = 0; block < entries.length; block += 1) {
            const key = JSON.stringify(entries[block]);
            const list = byKey.get(key) || [];
            list.push({ entry: entries[block], turn, block });
            byKey.set(key, list);
        }
    }
    context.renderedIndex = { turns: renderedTurns, byKey };
    return byKey;
}

function locateRenderedBlock(found, renderedTurns, projectedLength, context) {
    // sameBlock is JSON.stringify equality; the index is keyed by that string.
    const candidates = [...(renderedBlockIndex(renderedTurns, context).get(JSON.stringify(found.entry)) || [])];
    if (!candidates.length) return null;
    // The projection is a suffix of the rendered history when nothing drifted, so the
    // predicted rendered turn is the projected ordinal shifted by the length difference.
    const expectedTurn = found.turn + Math.max(0, renderedTurns.length - projectedLength);
    candidates.sort((a, b) => (
        Math.abs(a.turn - expectedTurn) - Math.abs(b.turn - expectedTurn)
        || (a.block === found.block ? 0 : 1) - (b.block === found.block ? 0 : 1)
        || a.turn - b.turn
        || a.block - b.block
    ));
    return candidates[0];
}

// When the projected prompt cannot supply the anchored block -- hydratePrompt can
// legitimately return an event with NO turns for a trace whose evidence anchors to
// tool calls -- resolve from the RENDERED turns instead, selected BY DIGEST: a
// candidate only qualifies if its addressed text hashes to exactly what the signal
// scanned, so byte-identity is proven before any offsets are trusted, and duplicate
// blocks cannot mislead the choice (every digest match is byte-identical content).
// The text a span addresses inside one block, or null when the block is not
// the kind of surface the span names. Shared by the digest fallback and the
// ordinal guard so both judge a block by the same bytes.
function addressedText(span, entry) {
    if (span.surface === "tool_call_argument" && entry?.type === "tool_use") {
        return pointer(entry.input, span.field_path || "");
    }
    if (span.surface === "tool_result_content" && entry?.type === "tool_result") {
        return pointer(entry.content, span.field_path || "");
    }
    if (span.surface === "message_text" && entry?.type === "text" && !span.field_path) {
        return entry.text;
    }
    return null;
}

async function digestMatches(span, entry, context) {
    const text = addressedText(span, entry);
    if (typeof text !== "string") return false;
    return (await sha256Hex(text, context)) === (span.content_sha256 || "").toLowerCase();
}

async function renderedDigestFallback(span, ref, renderedTurns, context) {
    const wantResult = span.surface === "tool_result_content";
    for (let turn = 0; turn < renderedTurns.length; turn += 1) {
        const entries = blocks(renderedTurns[turn]);
        for (let block = 0; block < entries.length; block += 1) {
            const entry = entries[block];
            if (typeof ref.tool_call_id === "string") {
                const matches = wantResult
                    ? entry?.type === "tool_result" && toolCallId(entry) === ref.tool_call_id
                    : entry?.type === "tool_use" && toolCallId(entry) === ref.tool_call_id;
                if (!matches) continue;
            }
            if (!(await digestMatches(span, entry, context))) continue;
            return { entry, turn, block };
        }
    }
    return null;
}

async function selectedSurface(span, trace, event, context) {
    const renderedTurns = Array.isArray(trace?.turns) ? trace.turns : [];
    const projectedTurns = Array.isArray(event?.turns) ? event.turns : [];
    const ref = span?.object;
    if (!ref || typeof ref !== "object") return null;
    if (ref.source_event_id !== String(trace?.id ?? event?.event_id ?? "")) return null;

    let found = null;
    let ordinalMismatch = false;
    if (typeof ref.tool_call_id === "string") {
        found = findToolBlock(projectedTurns, ref.tool_call_id, span.surface === "tool_result_content");
    } else if (ref.message && Number.isInteger(ref.message.turn_ordinal) && Number.isInteger(ref.message.block_ordinal)) {
        const turn = ref.message.turn_ordinal;
        const block = ref.message.block_ordinal;
        const entry = blocks(projectedTurns[turn])[block];
        // A message ordinal is positional: it counts turns of the projection the
        // signal scanned, and the turns on screen are not always that projection.
        // The trace axis grows at the front once the conversation record before
        // the capture folds in (completeTrace), and a provider's system prompt
        // may occupy a different number of leading turns in the rendered record
        // than in the scanned event. When the ordinal then lands on some OTHER
        // existing block, that block is byte-identical to itself and would be
        // accepted by locateRenderedBlock, skipping the digest search -- so the
        // ordinal hit is trusted only when it hashes to what the signal scanned.
        if (entry) {
            if (await digestMatches(span, entry, context)) found = { entry, turn, block };
            else ordinalMismatch = true;
        }
    } else if (typeof ref.event_path === "string") {
        found = eventPathBlock(projectedTurns, ref.event_path);
    }
    let located = found ? locateRenderedBlock(found, renderedTurns, projectedTurns.length, context) : null;
    if (!located) {
        located = await renderedDigestFallback(span, ref, renderedTurns, context);
        if (located) {
            // The scan text is the rendered block's own text: the digest just proved it is
            // byte-identical to the surface the signal scanned, so the span offsets apply.
            found = located;
        }
    }
    if (!located) {
        // The ordinal named a block that exists but is not what the signal scanned,
        // and nothing on the axis hashes to it either: the evidence and the record
        // disagree. Fail closed, and say so -- silently dropping a mark here is how
        // a real content drift would go unnoticed.
        if (ordinalMismatch) {
            console.error("Signal evidence content digest mismatch", {
                event_id: String(trace?.id ?? event?.event_id ?? ""),
                surface: span.surface,
            });
        }
        return null;
    }

    const renderedTurn = located.turn;
    const renderedEntry = located.entry;

    if (span.surface === "tool_call_argument" && found.entry?.type === "tool_use") {
        const text = pointer(found.entry.input, span.field_path || "");
        if (typeof text !== "string") return null;
        const projection = toolUseProjection(renderedEntry.input);
        const segment = projection.segments.find((item) => item.fieldPath === (span.field_path || ""));
        return segment ? {
            text,
            projection: { segments: [{ sourceStart: 0, sourceEnd: text.length, ...segment }], text: projection.text },
            loc: { surface: "tool_call", turn: renderedTurn, block: located.block },
        } : null;
    }
    if (span.surface === "tool_result_content" && found.entry?.type === "tool_result") {
        const text = pointer(found.entry.content, span.field_path || "");
        if (typeof text !== "string") return null;
        const projection = toolResultProjection(renderedEntry.content);
        const segment = projection.segments.find((item) => item.fieldPath === (span.field_path || ""));
        return segment ? {
            text,
            projection: { segments: [{ sourceStart: 0, sourceEnd: text.length, ...segment }], text: projection.text },
            loc: { surface: "tool_result", turn: renderedTurn, block: located.block },
        } : null;
    }
    if (span.surface === "message_text" && found.entry?.type === "text" && !span.field_path) {
        return typeof found.entry.text === "string" && renderedEntry.text === found.entry.text ? {
            text: found.entry.text,
            projection: {
                text: renderedEntry.text,
                segments: [{ sourceStart: 0, sourceEnd: found.entry.text.length, start: 0, end: renderedEntry.text.length }],
            },
            loc: { surface: "block", turn: renderedTurn, block: located.block },
        } : null;
    }
    return null;
}

async function sha256Hex(text, context) {
    let digest = context.digestCache.get(text);
    if (!digest) {
        digest = globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(text))
            .then((bytes) => Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, "0")).join(""));
        context.digestCache.set(text, digest);
    }
    return digest;
}

function scalars(text, context) {
    let value = context.scalarCache.get(text);
    if (!value) {
        value = Array.from(text);
        context.scalarCache.set(text, value);
    }
    return value;
}

async function resolveSpan(span, trace, event, context) {
    if (span?.unit !== "unicode_scalar"
        || !Number.isInteger(span.start)
        || !Number.isInteger(span.end)
        || span.start < 0
        || span.end <= span.start
        || typeof span.content_sha256 !== "string") return null;
    const selected = await selectedSurface(span, trace, event, context);
    if (!selected) return null;
    const sourceScalars = scalars(selected.text, context);
    if (span.end > sourceScalars.length) return null;
    if ((await sha256Hex(selected.text, context)) !== span.content_sha256.toLowerCase()) {
        console.error("Signal evidence content digest mismatch", {
            event_id: String(trace?.id ?? event?.event_id ?? ""),
            surface: span.surface,
        });
        return null;
    }
    const sourceStart = sourceScalars.slice(0, span.start).join("").length;
    const sourceEnd = sourceScalars.slice(0, span.end).join("").length;
    const match = selected.text.slice(sourceStart, sourceEnd);
    const segment = selected.projection.segments.find((item) => (
        sourceStart >= item.sourceStart && sourceEnd <= item.sourceEnd
    ));
    if (!segment) return null;
    const relativeStart = sourceStart - segment.sourceStart;
    const relativeEnd = sourceEnd - segment.sourceStart;
    const projectedStart = segment.sourceBoundaries?.[relativeStart] ?? relativeStart;
    const projectedEnd = segment.sourceBoundaries?.[relativeEnd] ?? relativeEnd;
    if (!Number.isInteger(projectedStart) || !Number.isInteger(projectedEnd)) return null;
    const start = segment.start + projectedStart;
    const end = segment.start + projectedEnd;
    const renderedText = segment.renderedText ?? selected.projection.text;
    if (renderedText.slice(start, end) !== match) return null;
    return { ...(selected.loc || segment.loc), start, end, match };
}

export async function persistedSignalHighlights(rows, trace, event) {
    const context = {
        digestCache: new Map(),
        scalarCache: new Map(),
    };
    const highlights = [];
    let incomplete = false;
    for (const row of Array.isArray(rows) ? rows : []) {
        const identity = highlightIdentity(row?.namespace);
        if (!identity) continue;
        if (["legacy", "unavailable", "invalid"].includes(row.evidence_state)) {
            incomplete = true;
            continue;
        }
        const parsed = parseEnvelope(row.evidence);
        if (parsed.kind !== "current") {
            incomplete = true;
            continue;
        }
        const { envelope } = parsed;
        if (envelope.evidence_truncated || Number(envelope.omitted_evidence_count) > 0) incomplete = true;
        // A finding with no evidence item at all is a highlight the dashboard
        // cannot draw, the same as a legacy row: the worker writes a version-1
        // envelope for every tag, so a signal version that predates span
        // evidence lands here with an empty list and no omission counters.
        if (!envelope.items.length) incomplete = true;
        const scores = sectionScores(envelope.items);
        const judge = judgeVerdict(envelope.items);
        const detail = identity.detailFromValue && typeof row.value === "string" && row.value
            ? row.value
            : null;
        const locsBySection = new Map();
        for (const item of envelope.items) {
            if (!item?.text_span) continue;
            const loc = await resolveSpan(item.text_span, trace, event, context);
            if (loc) {
                if (detail) loc.detail = detail;
                const section = Number.isInteger(item.section) ? item.section : null;
                const locs = locsBySection.get(section) || [];
                locs.push(loc);
                locsBySection.set(section, locs);
            }
            else incomplete = true;
        }
        for (const [section, locs] of locsBySection) {
            const highlight = {
                command: identity.command,
                type: `${identity.family}.${row.tag}`,
                value: { locs },
            };
            if (scores.has(section)) highlight.confidence = scores.get(section);
            if (judge) highlight.judge = judge;
            highlights.push(highlight);
        }
    }
    const status = incomplete ? (highlights.length ? "partial" : "unavailable") : "complete";
    return { highlights, status };
}

// Full source strings for an alert preview, with the same digest-verified spans
// used by the trace highlighter. The caller renders `text` itself and can make
// each range an evidence link without copying a secret into signal metadata.
export async function persistedCredentialEvidenceText(row, event) {
    if (row?.namespace !== "security.credscan" || !event) return [];
    const parsed = parseEnvelope(row.evidence);
    if (parsed.kind !== "current") return [];
    const context = { digestCache: new Map(), scalarCache: new Map() };
    const sources = new Map();

    for (const item of parsed.envelope.items) {
        const span = item?.text_span;
        if (span?.unit !== "unicode_scalar"
            || !Number.isInteger(span.start)
            || !Number.isInteger(span.end)
            || span.start < 0
            || span.end <= span.start
            || typeof span.content_sha256 !== "string") continue;
        const source = sourceSurface(span, event);
        if (!source) continue;
        const sourceScalars = scalars(source.text, context);
        if (span.end > sourceScalars.length) continue;
        if ((await sha256Hex(source.text, context)) !== span.content_sha256.toLowerCase()) continue;
        const start = sourceScalars.slice(0, span.start).join("").length;
        const end = sourceScalars.slice(0, span.end).join("").length;
        const current = sources.get(source.key) || { ...source, ranges: [] };
        current.ranges.push({ start, end });
        sources.set(source.key, current);
    }

    return [...sources.values()].map((source) => ({
        ...source,
        ranges: source.ranges
            .sort((a, b) => a.start - b.start || a.end - b.end)
            .filter((range, index, ranges) => index === 0 || range.start >= ranges[index - 1].end),
    }));
}
